import { createSlice } from '@reduxjs/toolkit';
import { notificationReceived } from '../notifications/notificationsSlice';
import { parseChannel } from '../notifications/notificationsUtils';
import { NOTIFICATION_KIND } from '../notifications/notificationsConstants';

/**
 * What the upload screen shows while the worker is creating the orders.
 *
 * The notification panel keeps only `{status, message, url}` of each event —
 * `describeEvent` drops the rest — so the counters and the failed rows cannot
 * come from there. This slice reads the same socket action the panel does,
 * which is the arrangement the dashboard slice already uses and the reason
 * `notificationReceived` is not swallowed by the panel's reducer.
 */
const initialState = {
  /** idle → sending (the POST) → running (the worker) → done, or stalled */
  phase: 'idle',
  total: 0,
  processed: 0,
  created: 0,
  failed: 0,
  errors: [],
  errorsUrl: null,
  message: '',
  /**
   * When the worker last said anything.
   *
   * Nothing else can tell a long file apart from a job that died: the task is
   * not recorded anywhere, so silence is silence. This is what the watchdog
   * measures against.
   */
  lastEventAt: null,
  /** Bytes of the file sent so far, and its size, while it goes up. */
  sent: 0,
  size: 0,
};

const orderUploadSlice = createSlice({
  name: 'orderUpload',
  initialState,
  reducers: {
    /** The file is on its way; the worker has not said anything yet. */
    orderUploadSending: () => ({ ...initialState, phase: 'sending' }),
    orderUploadSendProgressed: (state, action) => {
      if (state.phase !== 'sending' || !action.payload.size) return;
      state.sent = action.payload.sent;
      state.size = action.payload.size;
    },
    /** The backend took the file: from here on the socket does the talking. */
    orderUploadQueued: (state) => {
      state.phase = 'running';
      state.message = 'Archivo recibido. Procesando las órdenes...';
      state.lastEventAt = Date.now();
    },
    /**
     * Nothing has arrived for too long.
     *
     * Not the same as a failure: the worker may be alive and slow, or it may
     * have created orders and died before saying so. The screen says what is
     * true — that it stopped hearing — and sends the operator to look before
     * uploading the same file twice.
     */
    orderUploadStalled: (state) => {
      if (state.phase !== 'running') return;
      state.phase = 'stalled';
    },
    orderUploadCleared: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(notificationReceived, (state, action) => {
      const { channel, payload, role, userId } = action.payload;
      if (parseChannel(channel, role, userId) !== NOTIFICATION_KIND.ORDER_UPLOAD) return;

      state.message = payload?.message || state.message;
      state.total = payload?.total ?? state.total;
      state.processed = payload?.processed ?? state.processed;
      state.lastEventAt = Date.now();

      if (payload?.status === 'init' || payload?.status === 'processing') {
        // A late event revives a job the watchdog had already given up on.
        state.phase = 'running';
        return;
      }

      state.phase = 'done';
      state.created = payload?.created ?? 0;
      state.failed = payload?.failed ?? 0;
      state.errors = payload?.errors || [];
      state.errorsUrl = payload?.url || null;
      // The last progress message may never arrive, so the totals of the
      // closing event are what the bar ends on.
      state.processed = payload?.total ?? state.processed;
    });
  },
});

export const {
  orderUploadSending,
  orderUploadSendProgressed,
  orderUploadQueued,
  orderUploadStalled,
  orderUploadCleared,
} = orderUploadSlice.actions;

export const selectOrderUpload = (state) => state.orderUpload;

export default orderUploadSlice.reducer;
